import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import SplashScreenImg from "assets/img/splash_screen.svg";
import ErrorInformationPage from "components/ErrorScreens/ErrorInformationPage";
import { useAppDispatch, useAppSelector } from "store/store";
import { getEndpoint, ProctoringRoute } from "routes/route";
import mixpanel from "config/mixpanel";
import {
  AlvyEventState,
  ChatMessageRole,
  ErrorTypes,
  ErrorTypesMessages,
  SessionScope,
  SessionStatus
} from "globals/enums";
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import {
  AlertLogConfig,
  SessionTokenApiPayload,
  SessionTokenApiResponse,
  useGenerateSessionTokenQuery,
  useGenerateVerificationCodeQuery,
  VerificationTokenApiPayload
} from "services/session-token";
import { useInitializeAlvyChatMutation } from "services/message";
import {
  setChannelState,
  setClientConfiguration,
  setFireBaseState,
  setPrecheckStartTime,
  setPreviewStyle,
  setSessionStatus,
  setSessionTokenApiDetails,
  setUiErrorScreen,
  setVerificationTokenApiDetails,
  setIsTSBRedirecting
} from "store/app.slice";
import useRefreshSASToken from "hooks/useRefreshSASToken";
import { FirebaseInit } from "services/external/firebase";
import { useNavigate } from "react-router";
import {
  allowedStartBufferTime,
  defaultBlackListedSoftwaresMac,
  defaultBlackListedSoftwaresWindows,
  defaultRestrictedURLDomains,
  mixpanelToken,
  sbLaunchUrl
} from "config/constant";
import { getInitialData } from "helpers/common";
import { GlobalAppState } from "globals/interfaces";
import { channelApi } from "services/channel";
import * as Sentry from "@sentry/react";
import { getUserBrowser, isSupportedBrowser, isTsbBrowser } from "helpers/browser-helper";
import { compareTime } from "helpers/compare-time";
import { useCreatePingQuery } from "services/ping";
import { handleError } from "helpers/sentry";
import {
  AtxHandoffConfig,
  atxHandoffFor,
  forcedAtxHandoff,
  handoffToAtx,
  loadAtxBundle
} from "switcher";
import { updateCandidatePrecheckStatus } from "../helpers/firebase-update-helper";

declare global {
  interface Window {
    ProctorClient3: any;
    tv: any;
    pingIntervalId: NodeJS.Timer;
  }
}

const Splash = () => {
  const globalState: GlobalAppState = useAppSelector((state) => state.app);
  const navigate = useNavigate();
  const envDetails = getInitialData(window?.tv?.q[0], globalState.initScriptConfiguration);
  const {
    enforceTSB,
    redirectURL,
    blacklistedSoftwaresMac,
    blacklistedSoftwaresWindows,
    whitelistedSoftwaresWindows,
    whitelistedSoftwaresMac,
    minimizeOption,
    isScreenProtectionEnabled,
    restrictedURLDomains
  } = envDetails.clientConfiguration;
  const sessionDetails = useAppSelector((state: any) => state.app.session);
  const firebaseDb = globalState?.configuration?.firebase_db;
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const dispatch = useAppDispatch();
  const [sessionTokenApiPayload, setSessionTokenApiPayload] = useState<SessionTokenApiPayload>();

  const { data, error, refetch }: any = useCreatePingQuery(
    { session_token: sessionDetails?.session_token },
    {
      skip: !sessionDetails?.session_token || globalState.sessionStatus === "completed"
    }
  );

  useEffect(() => {
    dispatch(
      setClientConfiguration({
        additionalInstruction: envDetails?.clientConfiguration.additionalInstruction as string,
        referenceLinks: envDetails?.clientConfiguration.referenceLinks
      })
    );
    dispatch(
      setPreviewStyle({
        previewStyle: envDetails?.previewStyle
      })
    );
  }, []);

  const isProviewLoadedInTSB = isTsbBrowser();

  const constructFormData = () => ({
    RestrictedURLDomains: ((restrictedURLDomains || "") + defaultRestrictedURLDomains)
      .split(", ")
      .map((domain) => `.${domain.trim()}`)
      .join(", "),
    OnBeforeUnloadMessageText: "Leave site?",
    OnBeforeUnloadStayButtonText: "Cancel",
    OnBeforeUnloadLeaveButtonText: "Leave",
    CrashLogsCustomObj: {
      session_external_id: envDetails.session.external_id,
      attendee_external_id: envDetails.attendee_external_id
    },
    IsMinimizePermitted: minimizeOption
  });

  useEffect(() => {
    if (isProviewLoadedInTSB && enforceTSB) {
      const formData = {
        BlacklistedSoftwaresWindows:
          blacklistedSoftwaresWindows + defaultBlackListedSoftwaresWindows,
        BlacklistedSoftwaresMac: blacklistedSoftwaresMac + defaultBlackListedSoftwaresMac,
        WhitelistedSoftwaresWindows: whitelistedSoftwaresWindows,
        WhitelistedSoftwaresMac: whitelistedSoftwaresMac,
        IsScreenProtectionEnabled: isScreenProtectionEnabled,
        ...constructFormData()
      };
      window.parent.postMessage(
        {
          msg: "definerestrictions",
          ...formData
        },
        "*"
      );

      window.addEventListener(
        "message",
        function (event) {
          if (event.data.msg == "definerestrictionscompleted") {
            console.log("Successfully applied the settings via script");
          } else if (event.data.msg == "restrictedurldetected") {
            console.log("The URL detected is" + event.data.url);
            alertHelper(alertConfig).raiseAlert(
              AlertName.RestrictedURLDetected,
              dispatch,
              "",
              JSON.stringify({
                url: event.data.url
              })
            );
          } else if (event.data.msg == "newurldetected") {
            console.log("The URL detected is" + event.data.url);
            alertHelper(alertConfig).raiseAlert(
              AlertName.NewURLDetected,
              dispatch,
              "",
              JSON.stringify({
                url: event.data.url
              })
            );
          }
        },
        false
      );
    }
  }, [enforceTSB, isProviewLoadedInTSB]);

  useEffect(() => {
    if (!isProviewLoadedInTSB && enforceTSB && redirectURL) {
      dispatch(setIsTSBRedirecting({ isTSBRedirecting: true }));

      window.onbeforeunload = null;

      const encodedURl = encodeURIComponent(redirectURL);
      const addressUrl = `${sbLaunchUrl}?redirect_url=${decodeURIComponent(encodedURl)}`;
      window.location.replace(addressUrl);
    }
  }, [enforceTSB, redirectURL, isProviewLoadedInTSB]);

  const tokenPayload: VerificationTokenApiPayload = {
    proctor_token: envDetails.proctor_token,
    attendee_external_id: envDetails.attendee_external_id,
    attendee: envDetails.attendee,
    session: {
      external_id: envDetails.session.external_id,
      scope: envDetails.session.scope,
      title: envDetails.session.title,
      accommodations: envDetails.session.accommodations
    },
    configuration: {}
  };
  if (enforceTSB) {
    const enforce_tsb = {
      current_browser: isTsbBrowser() ? "TSB" : getUserBrowser(),
      redirect_url: redirectURL,
      blacklisted_softwares_windows:
        blacklistedSoftwaresWindows + defaultBlackListedSoftwaresWindows,
      blacklisted_softwares_mac: blacklistedSoftwaresMac + defaultBlackListedSoftwaresMac,
      whitelisted_softwares_windows: whitelistedSoftwaresWindows,
      whitelisted_softwares_mac: whitelistedSoftwaresMac,
      isScreenProtectionEnabled,
      ...constructFormData()
    };
    tokenPayload.configuration = { ...tokenPayload.configuration, enforce_tsb };
  }

  const {
    data: verificationCodeResponse,
    isSuccess: isVerificationCodeSuccess,
    isError: isVerificationCodeError,
    error: verificationCodeError
    // A forced-handoff build never runs this client's own flow, so none of its
    // calls are made either — the host page needs no valid proctor token.
  } = useGenerateVerificationCodeQuery(tokenPayload, { skip: !!forcedAtxHandoff });

  const {
    data: sessionTokenApiResult,
    isLoading: isSessionTokenFetching,
    isSuccess: isSessioTokenSuccess,
    isError: isSessionTokenError,
    error: sessionTokenError
  } = useGenerateSessionTokenQuery(sessionTokenApiPayload, {
    skip: !sessionTokenApiPayload
  });

  const [initializeAlvyChat, { isError: isAlvyChatInitError, isSuccess: isAlvyChatInitSuccess }] =
    useInitializeAlvyChatMutation();

  useRefreshSASToken();
  const [getChannel] = channelApi.endpoints.getChannel.useLazyQuery();

  const initializeFeatures = async (sessionTokenResponse: SessionTokenApiResponse) => {
    const sessionId = sessionTokenResponse?.session_id;
    const profileId = globalState?.initScriptConfiguration?.profile_id;
    if (sessionId) {
      try {
        Sentry.setTag("session_id", sessionId);
        Sentry.setUser({ session_id: sessionId, profile_id: profileId });
        const isMixpanel_analytics = sessionTokenResponse?.configuration?.mixpanel_analytics;
        if (mixpanelToken && isMixpanel_analytics) {
          const profileId = globalState?.initScriptConfiguration?.profile_id as string;
          mixpanel.init(sessionTokenResponse, profileId);
          mixpanel.identify(profileId);
          mixpanel.setProfile();
          mixpanel.track("proctoring client initiated");
          mixpanel.track(ProctoringRoute.TermsOfUse);
        }
        await initializeAlvyChat({
          step: "session_joined",
          candidate_instructions: sessionTokenResponse.instruction,
          state: AlvyEventState.Success,
          role: ChatMessageRole.System,
          role_to: [ChatMessageRole.Alvy]
        }).unwrap();
      } catch (error) {
        handleError(error);
      }
    }
  };

  useEffect(() => {
    if (!isSupportedBrowser()) {
      dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.BrowserNotSupported }));
      navigate(getEndpoint(ProctoringRoute.Error));
    }
  }, []);

  /**
   * Starts the v7 flow by asking for a session token. Skipping this is what
   * stops the v7 flow entirely when the session is handed to ATX — every step
   * after this one waits on the session token.
   */
  const startProctoringFlow = (verificationCode: string) => {
    setSessionTokenApiPayload({
      verification_token: verificationCode
    });
  };

  /**
   * A session mapped to ATX belongs to ATX. There is no fallback: if the bundle
   * cannot be loaded or started, the candidate stays on this splash screen and
   * the failure is reported, rather than being run through the v7 flow.
   *
   * Deliberate — running v7 after ATX has been reached risks two sessions for
   * one attempt (ATX creates its session during `init()`), and a v7 session for
   * an org whose proctoring config has already moved to ATX.
   */
  const handoffSessionToAtx = async (atx: AtxHandoffConfig) => {
    await loadAtxBundle();
    await handoffToAtx(atx, window?.tv?.q?.[0]);
  };

  // Test builds only: hand off immediately, without waiting on this client's
  // own API. See switcher/forced-handoff.ts — empty in every normal build.
  useEffect(() => {
    if (!forcedAtxHandoff) return;
    handoffSessionToAtx(forcedAtxHandoff).catch(handleError);
  }, []);

  useEffect(() => {
    if (isVerificationCodeSuccess) {
      const verificationCode = verificationCodeResponse.verification_code;
      dispatch(
        setVerificationTokenApiDetails({ verificationTokenApiResponse: verificationCodeResponse })
      );
      startProctoringFlow(verificationCode);
    }
  }, [isVerificationCodeSuccess, verificationCodeResponse, isVerificationCodeError]);

  useEffect(() => {
    if (isSessioTokenSuccess) {
      (async () => {
        const sessionTokenResult = sessionTokenApiResult as unknown as SessionTokenApiResponse;

        // A mapped session belongs to ATX from here. Bail before storing the
        // token: that is what starts ping polling and unblocks the navigation
        // into this client's flow, neither of which should happen.
        const atx = atxHandoffFor(sessionTokenResult, enforceTSB);
        if (atx) {
          handoffSessionToAtx(atx).catch(handleError);
          return;
        }

        dispatch(setSessionTokenApiDetails({ sessionTokenApiResponse: sessionTokenResult }));
        await initializeFeatures(sessionTokenResult);
        alertHelper(alertConfig).raiseAlert(AlertName.SessionJoined, dispatch);
        if (sessionTokenResult.session_type === SessionScope.LiveProctor) {
          const fetchChannelData = async () => {
            try {
              const channel = await getChannel().unwrap();
              dispatch(setChannelState(channel));
              await updateCandidatePrecheckStatus(
                sessionTokenResult.session_id,
                false,
                sessionTokenResult.session_type,
                channel.participant_group_id
              );
            } catch (error) {
              handleError(`[Splash] Error occurred while channel: ${error}`);
            }
          };
          await fetchChannelData();
        } else {
          await updateCandidatePrecheckStatus(
            sessionTokenResult.session_id,
            false,
            sessionTokenResult.session_type
          );
        }
      })();
    }
  }, [isSessioTokenSuccess, sessionTokenApiResult]);

  const calculateTimeLeft = async (appDetails) => {
    const pingApiResponse = await refetch();
    const currentTime = new Date(`${pingApiResponse.data}`);
    const examStartTime = new Date(appDetails?.configuration?.valid_from);
    examStartTime.setMinutes(
      examStartTime.getMinutes() -
      allowedStartBufferTime -
      appDetails?.configuration?.start_buffer_time
    );
    const timeLeft = compareTime(currentTime, examStartTime);
    if (timeLeft > 0) {
      dispatch(
        setPrecheckStartTime({
          precheckStartTime: allowedStartBufferTime + appDetails?.configuration?.start_buffer_time
        })
      );
      dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.TestNotStarted }));
      navigate(getEndpoint(ProctoringRoute.Error));
      return;
    }
  };

  useEffect(() => {
    if (isSessioTokenSuccess && sessionDetails?.session_token && isAlvyChatInitSuccess) {
      if (enforceTSB && !isProviewLoadedInTSB) {
        return;
      }
      const sessionTokenResult = sessionTokenApiResult as SessionTokenApiResponse;
      if (sessionTokenResult?.session_type === SessionScope.LiveProctor) {
        calculateTimeLeft(globalState);
      }
      firebaseDb && FirebaseInit(sessionTokenResult?.session_id as number, dispatch, { navigate });
      navigate(getEndpoint(ProctoringRoute.TermsOfUse));
      return;
    } else if (isSessionTokenError || isVerificationCodeError || isAlvyChatInitError) {
      const errorObj: any = sessionTokenError || verificationCodeError;
      let errorType, fireBaseSessionStatus;
      if (errorObj?.status === 403) {
        switch (errorObj?.data?.message) {
          case ErrorTypesMessages.SessionTerminationError:
            errorType = ErrorTypes.TerminatedError;
            dispatch(setSessionStatus({ status: SessionStatus.Terminated }));
            fireBaseSessionStatus = SessionStatus.Terminated;
            break;
          case ErrorTypesMessages.NoShowError:
            errorType = ErrorTypes.NoShowError;
            fireBaseSessionStatus = SessionStatus.NoShow;
            break;
          case ErrorTypesMessages.SessionCompletedError:
            errorType = ErrorTypes.SessionCompleted;
            dispatch(setSessionStatus({ status: SessionStatus.Completed }));
            fireBaseSessionStatus = SessionStatus.Completed;
            break;
          case ErrorTypesMessages.SlotTimeAlreadyPassed:
            errorType = ErrorTypes.SlotTimeAlreadyPassed;
            break;
          default:
            break;
        }
      } else if (errorObj?.status === 404) {
        errorType = ErrorTypes.SessionNotScheduled;
      } else {
        errorType = ErrorTypes.ProviewLoadingError;
      }
      fireBaseSessionStatus &&
        dispatch(setFireBaseState({ status: fireBaseSessionStatus as string }));
      dispatch(setUiErrorScreen({ uiErrorScreen: errorType as string }));
      navigate(getEndpoint(ProctoringRoute.Error));
    }
  }, [
    sessionTokenApiResult,
    isSessionTokenFetching,
    isSessioTokenSuccess,
    isSessionTokenError,
    isAlvyChatInitSuccess,
    isAlvyChatInitError,
    firebaseDb,
    isVerificationCodeError,
    sessionDetails,
    enforceTSB,
    isProviewLoadedInTSB
  ]);

  useEffect(() => {
    if (
      error?.data?.status === 403 &&
      error?.data?.message === "Session Token is not active or is deleted" &&
      globalState.sessionStatus !== "completed"
    ) {
      dispatch(setUiErrorScreen({ uiErrorScreen: ErrorTypes.SessionExpired as string }));
      navigate(getEndpoint(ProctoringRoute.Error));
    }
  }, [sessionDetails, data, error]);

  if (globalState.isTSBRedirecting && !isProviewLoadedInTSB) {
    return (
      <div className="tvp-mx-auto" id="basediv">
        <div className="tvp-rounded-lg tvp-py-28 tvp-px-32 tvp-bg-white">
          <ErrorInformationPage
            heading={<FormattedMessage id="component.tsbRequired.header" />}
            subheading={<FormattedMessage id="component.tsbRequired.subheader" />}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="tvp-mx-auto" id="basediv">
        {SplashScreenImg && (
          <div className="tvp-rounded-lg tvp-py-28 tvp-px-32 tvp-bg-white">
            <img src={SplashScreenImg} alt="Proview screen" />
          </div>
        )}
      </div>
    </>
  );
};

export default Splash;
